import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Gauge, User, Calendar, Fuel, Shield, MessageSquare, Phone, AlertCircle, Loader2 } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useAuth } from "@/lib/auth";
import { useChat } from "@/lib/chat";
import { api, INR, INR_SHORT } from "@/lib/marketplace";

export default function ListingDetail() {
  const { id } = useParams();
  const { user, login } = useAuth();
  const chat = useChat();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [loginModal, setLoginModal] = useState(false);

  const openChat = () => { if (!user) setLoginModal(true); else chat?.openChat(item.listing_id, item.seller_display_name); };

  useEffect(() => {
    document.title = "Listing — Biker Boulevard";
    window.scrollTo(0, 0);
    (async () => {
      try {
        const d = await api.getListing(id);
        setItem(d);
        setImgIdx(d.primary_photo_index || 0);
        document.title = `${d.brand} ${d.model} — Biker Boulevard`;
      } catch { setError("Listing not found or no longer available."); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) {
    return <PageShell><div className="pt-32 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00cccc] mx-auto" /></div></PageShell>;
  }
  if (error) {
    return (
      <PageShell>
        <div className="pt-32 text-center">
          <AlertCircle className="w-10 h-10 text-zinc-500 mx-auto" />
          <p className="mt-4 text-zinc-400">{error}</p>
          <Link to="/marketplace" className="mt-6 inline-block px-5 py-2.5 rounded-full border border-[#00cccc]/40 text-[#00cccc]">Back to marketplace</Link>
        </div>
      </PageShell>
    );
  }

  const photos = item.photo_ids || [];
  const nav = (d) => setImgIdx((i) => (i + d + photos.length) % photos.length);

  return (
    <PageShell>
      <div className="pt-24 pb-20 max-w-[1200px] mx-auto px-5 md:px-10" data-testid="listing-detail-page">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-[#00cccc] mb-5">
          <ChevronLeft className="w-4 h-4" /> Back to marketplace
        </Link>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-8">
          {/* Gallery */}
          <div>
            <div className="relative rounded-2xl overflow-hidden bg-[#1A1E23] border border-white/10 aspect-[4/3]">
              {photos.length > 0 ? (
                <img src={api.photoUrl(photos[imgIdx])} alt={`Photo ${imgIdx + 1}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-600">No photos</div>
              )}
              {photos.length > 1 && (
                <>
                  <button onClick={() => nav(-1)} data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black text-white border border-white/20 flex items-center justify-center">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button onClick={() => nav(1)} data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black text-white border border-white/20 flex items-center justify-center">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-mono bg-black/70 text-white border border-white/20">
                    {imgIdx + 1} / {photos.length}
                  </div>
                </>
              )}
              {item.verified && (
                <span className="absolute top-3 left-3 px-2 py-1 rounded-full text-[10px] font-mono bg-[#00cccc] text-black font-semibold">BB VERIFIED</span>
              )}
            </div>
            {photos.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {photos.map((p, i) => (
                  <button key={p} onClick={() => setImgIdx(i)}
                    className={`aspect-square rounded-lg overflow-hidden border transition-all ${i === imgIdx ? "border-[#00cccc]" : "border-white/10 opacity-70 hover:opacity-100"}`}
                    data-testid={`thumb-${i}`}>
                    <img src={api.photoUrl(p)} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <div>
              <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-zinc-500">{item.registration_year} &middot; {item.brand}</div>
              <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-white">{item.brand} {item.model} {item.variant}</h1>
              <div className="mt-2 text-sm text-zinc-400 flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {item.seller_city}, {item.seller_state}</div>
            </div>

            <div className="p-5 rounded-2xl border border-[#00cccc]/25 bg-[#00cccc]/[0.04]">
              <div className="text-xs font-sans font-semibold uppercase tracking-wider text-zinc-300 mb-1">Seller Asking</div>
              <div className="text-3xl font-bold text-[#00cccc]">{INR(item.expected_price)}</div>
              {item.valuation && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-xs font-sans font-semibold uppercase tracking-wider text-zinc-300">BB Estimated Market Value</div>
                  <div className="text-lg text-white font-mono mt-1">{INR_SHORT(item.valuation.low)} – {INR_SHORT(item.valuation.high)}</div>
                </div>
              )}
            </div>

            <div className="p-5 rounded-2xl border border-white/10 bg-white/[0.03] space-y-3">
              <div className="text-xs text-zinc-500 font-mono">Listed by <span className="text-white font-semibold">{item.seller_display_name}</span></div>
              <button onClick={openChat} data-testid="chat-with-seller"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#00cccc] text-black font-semibold hover:shadow-[0_0_28px_rgba(0,204,204,0.6)] transition-all">
                <MessageSquare className="w-4 h-4" /> Chat with Seller
              </button>
              <p className="text-[11px] text-zinc-500 text-center">Login required to contact the seller.</p>
            </div>
          </div>
        </div>

        {/* Specs */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            [Calendar, "Year", item.registration_year],
            [Gauge, "KMs Driven", item.kms_driven?.toLocaleString("en-IN")],
            [User, "Owners", item.previous_owners],
            [Fuel, "Fuel", item.fuel_type || "—"],
            [Shield, "Condition", item.condition],
            [null, "CC", item.engine_cc || "—"],
            [null, "Reg City", item.registration_city],
          ].map(([Icon, k, v]) => (
            <div key={k} className="p-4 rounded-xl bg-white/[0.04] border border-white/10">
              <div className="flex items-center gap-2 text-xs font-sans font-semibold uppercase tracking-wider text-zinc-300">
                {Icon && <Icon className="w-3 h-3 text-[#00cccc]" />}{k}
              </div>
              <div className="mt-2 text-sm text-white">{v || "—"}</div>
            </div>
          ))}
        </div>

        {(item.modifications || item.additional_notes) && (
          <div className="mt-8 p-6 rounded-2xl border border-white/10 bg-white/[0.03] space-y-4">
            {item.modifications && (<div><div className="text-[10px] font-mono tracking-widest uppercase text-[#00cccc]">// Modifications</div><p className="mt-1 text-sm text-zinc-300">{item.modifications}</p></div>)}
            {item.additional_notes && (<div><div className="text-[10px] font-mono tracking-widest uppercase text-[#00cccc]">// Notes</div><p className="mt-1 text-sm text-zinc-300">{item.additional_notes}</p></div>)}
            <div className="pt-3 border-t border-white/10 text-xs text-zinc-500">
              Accident history: <span className="text-white">{item.accident_history ? "Yes" : "No"}</span> &nbsp;·&nbsp;
              Loan: <span className="text-white">{item.has_loan ? "Yes" : "No"}</span> &nbsp;·&nbsp;
              Pending challan: <span className="text-white">{item.has_challan ? "Yes" : "No"}</span>
            </div>
          </div>
        )}
      </div>

      {loginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="login-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setLoginModal(false)} />
          <div className="relative max-w-md w-full p-7 rounded-2xl bg-[#15181C] border border-[#00cccc]/30">
            <h3 className="text-xl font-bold">Login to contact the seller</h3>
            <p className="mt-2 text-sm text-zinc-400">Create a free Biker Boulevard account to chat with motorcycle sellers.</p>
            <button onClick={login} className="mt-6 w-full px-5 py-3 rounded-full bg-[#00cccc] text-black font-semibold hover:shadow-[0_0_28px_rgba(0,204,204,0.6)] transition-all" data-testid="login-with-google">
              Continue with Google
            </button>
            <button onClick={() => setLoginModal(false)} className="mt-3 w-full px-5 py-3 rounded-full border border-white/20 text-white hover:border-[#00cccc]/50 transition-all text-sm">
              Not now
            </button>
            <p className="mt-4 text-[11px] text-zinc-500 text-center">We only use your email to let the seller reply. No posts, no spam.</p>
          </div>
        </div>
      )}

      <Footer />
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <main className="relative bg-[#0D0F12] text-white min-h-screen">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} />
        <div className="absolute inset-0 grid-bg opacity-10" />
      </div>
      {children}
    </main>
  );
}
